import { createSignal } from "solid-js";
import type { CatalogExercise, Exercise } from "../api/types";
import { normalizeType, rowIssue, rowType, type ExerciseType } from "./activity";
import { todayISO } from "./repeatWorkout";

const STORAGE_KEY = "alex-tracker:active-workout";

export type WeightUnit = "kg" | "lb";

export interface ActiveSet {
  id: string;
  reps: number;
  weight: number | null;
  unit: WeightUnit;
  done: boolean;
}

export interface ActiveCircuit {
  id: string;
  /** How many times to walk the circuit exercises (min 1). */
  rounds: number;
}

export interface ActiveExercise {
  id: string;
  name: string;
  notes: string;
  muscleGroups: string[];
  /** Catalog link when resolved/created; null until user picks or creates. */
  catalogExerciseId: string | null;
  /**
   * Activity type (missing in sessions saved before step 1 = strength).
   * Non-strength exercises keep ONE set whose `done` flag is the "done" mark;
   * their values live in the fields below.
   */
  exerciseType?: ExerciseType;
  icon?: string | null;
  durationSec?: number | null;
  distanceM?: number | null;
  intensity?: number | null;
  rounds?: number | null;
  /** UI only: unreadable field text (blocks finishing). */
  invalid?: { duration?: boolean; distance?: boolean };
  /** When set, exercise is part of a circuit group. */
  circuitId: string | null;
  /**
   * Sets finished in previous circuit rounds (current visit uses `sets[].done`).
   * Used so multi-round circuits don't under-count on save.
   */
  bankedSets: number;
  sets: ActiveSet[];
}

export interface ActiveWorkoutSession {
  title: string;
  notes: string;
  startedAt: string;
  /** When non-null, the workout clock is paused at this moment */
  pausedAt: string | null;
  /** Total ms spent paused (completed pause segments) */
  pauseAccumMs: number;
  plannedId: string | null;
  sourceLabel: string | null;
  exercises: ActiveExercise[];
  circuits: ActiveCircuit[];
  /** Currently focused exercise (Focus Mode). */
  focusExerciseId: string | null;
  /** Current circuit round when focus is inside a circuit (1-based). */
  focusRound: number;
  updatedAt: string;
}

/** One step in the expanded play order (circuits expand by rounds). */
export interface PlayStep {
  exerciseId: string;
  circuitId: string | null;
  round: number;
  /** Total rounds for this circuit (1 for standalone). */
  totalRounds: number;
}

function uid(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}

export function createEmptySet(
  defaults?: Partial<Pick<ActiveSet, "reps" | "weight" | "unit">>,
): ActiveSet {
  return {
    id: uid("set"),
    reps: defaults?.reps ?? 10,
    weight: defaults?.weight ?? null,
    unit: defaults?.unit ?? "kg",
    done: false,
  };
}

export function exerciseFromPlan(ex: Exercise): ActiveExercise {
  const type = rowType(ex);
  if (type !== "strength") {
    return {
      id: uid("ex"),
      name: ex.name ?? "",
      notes: ex.notes ?? "",
      muscleGroups: [...(ex.muscleGroups ?? [])],
      catalogExerciseId: ex.catalogExerciseId ?? null,
      exerciseType: type,
      icon: ex.icon ?? null,
      durationSec: ex.durationSec ?? null,
      distanceM: ex.distanceM ?? null,
      intensity: ex.intensity ?? null,
      rounds: ex.rounds ?? null,
      circuitId: null,
      bankedSets: 0,
      sets: [createEmptySet({ reps: 1 })],
    };
  }
  const count = Math.max(1, Math.min(20, ex.sets || 1));
  const sets: ActiveSet[] = [];
  for (let i = 0; i < count; i++) {
    sets.push(
      createEmptySet({
        reps: ex.reps || 10,
        weight: ex.weight ?? null,
        unit: "kg",
      }),
    );
  }
  return {
    id: uid("ex"),
    name: ex.name ?? "",
    notes: ex.notes ?? "",
    muscleGroups: [...(ex.muscleGroups ?? [])],
    catalogExerciseId: ex.catalogExerciseId ?? null,
    exerciseType: "strength",
    icon: ex.icon ?? null,
    circuitId: null,
    bankedSets: 0,
    sets,
  };
}

/** Activity type of an active exercise. */
export function activeType(ex: ActiveExercise): ExerciseType {
  return normalizeType(ex.exerciseType);
}

/** Apply a picked catalog exercise; values of another type are cleared. */
export function applyCatalogToActive(
  ex: ActiveExercise,
  item: CatalogExercise,
): ActiveExercise {
  const next: ActiveExercise = {
    ...ex,
    name: item.name,
    muscleGroups: [...(item.muscleGroups ?? [])],
    catalogExerciseId: item.id,
    exerciseType: item.exerciseType,
    icon: item.icon,
  };
  if (activeType(ex) === item.exerciseType) return next;
  return {
    ...next,
    durationSec: null,
    distanceM: null,
    intensity: null,
    rounds: null,
    invalid: undefined,
    bankedSets: 0,
    sets:
      item.exerciseType === "strength"
        ? [createEmptySet(), createEmptySet(), createEmptySet()]
        : [createEmptySet({ reps: 1 })],
  };
}

function activeIssue(ex: ActiveExercise) {
  return rowIssue({
    exerciseType: activeType(ex),
    durationSec: ex.durationSec,
    distanceM: ex.distanceM,
    invalid: ex.invalid,
  });
}

/** Names of non-strength exercises still missing their required value. */
export function exercisesMissingValues(exercises: ActiveExercise[]): string[] {
  return exercises
    .filter((ex) => ex.name.trim() && activeType(ex) !== "strength")
    .filter((ex) => activeIssue(ex))
    .map((ex) => ex.name.trim());
}

export function createBlankExercise(): ActiveExercise {
  return {
    id: uid("ex"),
    name: "",
    notes: "",
    muscleGroups: [],
    catalogExerciseId: null,
    circuitId: null,
    bankedSets: 0,
    sets: [createEmptySet(), createEmptySet(), createEmptySet()],
  };
}

export function createFreshSession(opts?: {
  title?: string;
  notes?: string;
  plannedId?: string | null;
  sourceLabel?: string | null;
  exercises?: Exercise[];
}): ActiveWorkoutSession {
  const now = new Date().toISOString();
  const fromPlan = opts?.exercises?.length
    ? opts.exercises.map(exerciseFromPlan)
    : [];
  return {
    title: opts?.title?.trim() || defaultTitle(),
    notes: opts?.notes ?? "",
    startedAt: now,
    pausedAt: null,
    pauseAccumMs: 0,
    plannedId: opts?.plannedId ?? null,
    sourceLabel: opts?.sourceLabel ?? null,
    exercises: fromPlan,
    circuits: [],
    focusExerciseId: fromPlan[0]?.id ?? null,
    focusRound: 1,
    updatedAt: now,
  };
}

/** e.g. "2026-07-23 18:45" — date + local start time */
function defaultTitle(d = new Date()) {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${todayISO(d)} ${hh}:${mm}`;
}

function normalizeExercise(ex: ActiveExercise): ActiveExercise {
  return {
    ...ex,
    catalogExerciseId: ex.catalogExerciseId ?? null,
    exerciseType: normalizeType(ex.exerciseType),
    circuitId: ex.circuitId ?? null,
    bankedSets: Number.isFinite(ex.bankedSets) ? Math.max(0, ex.bankedSets) : 0,
    muscleGroups: Array.isArray(ex.muscleGroups) ? ex.muscleGroups : [],
    sets: Array.isArray(ex.sets) && ex.sets.length
      ? ex.sets
      : [createEmptySet()],
  };
}

function normalizeSession(data: ActiveWorkoutSession): ActiveWorkoutSession {
  const exercises = Array.isArray(data.exercises)
    ? data.exercises.map(normalizeExercise)
    : [];
  const circuits = Array.isArray(data.circuits)
    ? data.circuits
        .filter((c) => c && typeof c.id === "string")
        .map((c) => ({
          id: c.id,
          rounds: Math.max(1, Math.min(20, Math.round(Number(c.rounds) || 1))),
        }))
    : [];
  // Drop circuit refs that no longer exist
  const circuitIds = new Set(circuits.map((c) => c.id));
  const cleaned = exercises.map((ex) =>
    ex.circuitId && !circuitIds.has(ex.circuitId)
      ? { ...ex, circuitId: null }
      : ex,
  );
  let focusExerciseId = data.focusExerciseId ?? cleaned[0]?.id ?? null;
  if (focusExerciseId && !cleaned.some((e) => e.id === focusExerciseId)) {
    focusExerciseId = cleaned[0]?.id ?? null;
  }
  const focusRound = Math.max(1, Math.round(Number(data.focusRound) || 1));
  return {
    ...data,
    pausedAt: data.pausedAt ?? null,
    pauseAccumMs: Number.isFinite(data.pauseAccumMs) ? data.pauseAccumMs : 0,
    exercises: cleaned,
    circuits,
    focusExerciseId,
    focusRound,
  };
}

function loadFromStorage(): ActiveWorkoutSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as ActiveWorkoutSession;
    if (!data || typeof data !== "object" || !data.startedAt) return null;
    if (!Array.isArray(data.exercises)) return null;
    return normalizeSession(data);
  } catch {
    return null;
  }
}

function persist(session: ActiveWorkoutSession | null) {
  try {
    if (!session) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* quota / private mode */
  }
}

const [session, setSessionSignal] = createSignal<ActiveWorkoutSession | null>(
  loadFromStorage(),
);

export function getActiveSession() {
  return session();
}

export function activeSessionSignal() {
  return session;
}

export function hasActiveSession() {
  return session() != null;
}

function commit(next: ActiveWorkoutSession | null) {
  if (next) {
    next = { ...next, updatedAt: new Date().toISOString() };
  }
  setSessionSignal(next);
  persist(next);
  return next;
}

export function startActiveSession(opts?: {
  title?: string;
  notes?: string;
  plannedId?: string | null;
  sourceLabel?: string | null;
  exercises?: Exercise[];
  /** Replace any existing session without confirmation (caller confirmed). */
  force?: boolean;
}): ActiveWorkoutSession {
  if (session() && !opts?.force) {
    return session()!;
  }
  return commit(createFreshSession(opts))!;
}

export function resumeOrStart(opts?: {
  title?: string;
  notes?: string;
  plannedId?: string | null;
  sourceLabel?: string | null;
  exercises?: Exercise[];
}): ActiveWorkoutSession {
  const existing = session();
  if (existing) return existing;
  return startActiveSession({ ...opts, force: true });
}

export function updateActiveSession(
  patch: Partial<
    Pick<
      ActiveWorkoutSession,
      | "title"
      | "notes"
      | "exercises"
      | "circuits"
      | "focusExerciseId"
      | "focusRound"
      | "plannedId"
      | "sourceLabel"
      | "pausedAt"
      | "pauseAccumMs"
    >
  >,
) {
  const cur = session();
  if (!cur) return null;
  return commit(normalizeSession({ ...cur, ...patch }));
}

export function setActiveExercises(exercises: ActiveExercise[]) {
  return updateActiveSession({ exercises });
}

export function isWorkoutPaused(s?: ActiveWorkoutSession | null) {
  const cur = s ?? session();
  return Boolean(cur?.pausedAt);
}

export function pauseActiveWorkout() {
  const cur = session();
  if (!cur || cur.pausedAt) return cur;
  return commit({ ...cur, pausedAt: new Date().toISOString() });
}

export function resumeActiveWorkout() {
  const cur = session();
  if (!cur?.pausedAt) return cur;
  const pausedMs = Date.now() - new Date(cur.pausedAt).getTime();
  return commit({
    ...cur,
    pausedAt: null,
    pauseAccumMs: (cur.pauseAccumMs || 0) + Math.max(0, pausedMs),
  });
}

export function clearActiveSession() {
  commit(null);
}

/** Expand session into linear play order (circuits × rounds). */
export function buildPlayOrder(s: ActiveWorkoutSession): PlayStep[] {
  const roundsById = new Map(s.circuits.map((c) => [c.id, c.rounds]));
  const steps: PlayStep[] = [];
  const list = s.exercises;
  let i = 0;
  while (i < list.length) {
    const ex = list[i]!;
    if (!ex.circuitId) {
      steps.push({
        exerciseId: ex.id,
        circuitId: null,
        round: 1,
        totalRounds: 1,
      });
      i++;
      continue;
    }
    const cid = ex.circuitId;
    const group: ActiveExercise[] = [];
    while (i < list.length && list[i]!.circuitId === cid) {
      group.push(list[i]!);
      i++;
    }
    const totalRounds = Math.max(1, roundsById.get(cid) ?? 1);
    for (let r = 1; r <= totalRounds; r++) {
      for (const g of group) {
        steps.push({
          exerciseId: g.id,
          circuitId: cid,
          round: r,
          totalRounds,
        });
      }
    }
  }
  return steps;
}

export function findPlayStepIndex(
  steps: PlayStep[],
  exerciseId: string | null,
  round: number,
): number {
  if (!exerciseId) return -1;
  const r = Math.max(1, round);
  const exact = steps.findIndex(
    (st) => st.exerciseId === exerciseId && st.round === r,
  );
  if (exact >= 0) return exact;
  return steps.findIndex((st) => st.exerciseId === exerciseId);
}

export function exerciseAllSetsDone(ex: ActiveExercise): boolean {
  return ex.sets.length > 0 && ex.sets.every((st) => st.done);
}

/** True when every set of every exercise is done for the current visit
 *  (circuit banking may leave bankedSets > 0 with fresh sets). */
export function isExerciseVisitDone(ex: ActiveExercise): boolean {
  return exerciseAllSetsDone(ex);
}

/**
 * Bank done sets from the current visit, reset checkmarks for a new visit,
 * keep weight/reps. Used when leaving an exercise for the next circuit round.
 */
export function bankAndResetExercise(ex: ActiveExercise): ActiveExercise {
  const doneCount = ex.sets.filter((st) => st.done).length;
  return {
    ...ex,
    bankedSets: (ex.bankedSets || 0) + doneCount,
    sets: ex.sets.map((st) => ({ ...st, done: false })),
  };
}

export function createCircuit(rounds = 3): ActiveCircuit {
  return {
    id: uid("circuit"),
    rounds: Math.max(1, Math.min(20, rounds)),
  };
}

/** Active ms of the session excluding paused time. */
export function activeElapsedMs(
  s: ActiveWorkoutSession,
  now = Date.now(),
): number {
  const start = new Date(s.startedAt).getTime();
  if (!Number.isFinite(start)) return 0;
  let pause = Number.isFinite(s.pauseAccumMs) ? s.pauseAccumMs : 0;
  if (s.pausedAt) {
    const p = new Date(s.pausedAt).getTime();
    if (Number.isFinite(p)) pause += Math.max(0, now - p);
  }
  return Math.max(0, now - start - pause);
}

/** Convert active session → API exercise payload (done sets preferred). */
export function toApiExercises(exercises: ActiveExercise[]): Exercise[] {
  const out: Exercise[] = [];

  for (const ex of exercises) {
    const name = ex.name.trim();
    if (!name) continue;
    const type = activeType(ex);
    if (type !== "strength") {
      // Needs its required value; the page asks for it before finishing.
      if (activeIssue(ex)) continue;
      out.push({
        name,
        sets: 1,
        reps: 1,
        weight: null,
        exerciseType: type,
        durationSec: ex.durationSec ?? null,
        distanceM: ex.distanceM ?? null,
        intensity: ex.intensity ?? null,
        rounds: ex.rounds ?? null,
        muscleGroups: ex.muscleGroups ?? [],
        notes: ex.notes?.trim() || null,
        sortOrder: out.length,
        catalogExerciseId: ex.catalogExerciseId ?? null,
      });
      continue;
    }
    const done = ex.sets.filter((st) => st.done);
    const currentDone = done.length;
    const banked = ex.bankedSets || 0;
    const totalSets = banked + currentDone;
    if (totalSets <= 0 && !ex.sets.length) continue;
    const source = done.length > 0 ? done : ex.sets;
    if (!source.length && totalSets <= 0) continue;
    const last = source[source.length - 1] ?? ex.sets[ex.sets.length - 1];
    if (!last) continue;
    out.push({
      name,
      sets: Math.max(1, totalSets > 0 ? totalSets : source.length),
      reps: Math.max(1, Math.round(last.reps) || 1),
      weight: last.weight,
      exerciseType: "strength",
      muscleGroups: ex.muscleGroups ?? [],
      notes: ex.notes?.trim() || null,
      sortOrder: out.length,
      catalogExerciseId: ex.catalogExerciseId ?? null,
    });
  }
  return out;
}

export function durationMinutesFromSession(
  s: ActiveWorkoutSession,
  finishedAt = new Date(),
): number {
  // If currently paused, count the open pause segment as paused too
  const ms = activeElapsedMs(s, finishedAt.getTime());
  const mins = Math.round(ms / 60000);
  return Math.max(1, mins);
}

export function formatElapsed(
  s: ActiveWorkoutSession | string,
  now = Date.now(),
): string {
  const ms =
    typeof s === "string"
      ? Math.max(0, now - new Date(s).getTime())
      : activeElapsedMs(s, now);
  if (!Number.isFinite(ms)) return "0:00";
  const sec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const ss = sec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  }
  return `${m}:${String(ss).padStart(2, "0")}`;
}
